import { ReactNode, useMemo } from 'react';
import { Box, Button as CKButton, ButtonProps as CKButtonProps } from '@chakra-ui/react';

type Color = 'primary' | 'secondary' | 'alert' | 'default' | 'none';

export type ButtonProps = CKButtonProps & {
  color?: Color;
  small?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  gap?: boolean;
};

export const Button = ({
  children,
  small,
  color: colorProp = 'default',
  startAdornment,
  endAdornment,
  gap,
  ...props
}: ButtonProps) => {
  const { isDisabled, variant } = props;

  const { color, base, hover } = useMemo(() => {
    if (isDisabled) {
      return {
        base: 'brand.disabled',
        color: 'brand.dark-green',
        hover: 'brand.disabled',
      };
    }
    if (variant === 'link') {
      return {
        base: 'transparent',
        hover: 'brand.tan-100',
        color: 'brand.black',
      };
    }

    return {
      default: {
        base: 'brand.tan-200',
        hover: 'brand.tan-300',
        color: 'brand.dark-green',
      },
      primary: {
        base: 'brand.army-green',
        hover: 'brand.army-green-hover',
        color: 'brand.white',
      },
      secondary: {
        base: 'brand.light-green',
        hover: 'brand.light-green-hover',
        color: 'brand.army-green',
      },
      none: {
        base: 'none',
        hover: 'brand.army-green',
        color: 'none',
      },
      alert: { base: 'brand.alert', hover: 'brand.alert-hover', color: 'brand.white' },
    }[colorProp as Color];
  }, [colorProp, isDisabled, variant]);

  return (
    <CKButton
      disabled={isDisabled}
      {...props}
      sx={{
        color,
        display: 'flex',
        justifyContent: gap ? 'space-between' : 'center',
        alignItems: 'center',
        borderRadius: small ? 4 : 8,
        fontSize: small ? 12 : 14,
        fontWeight: 600,
        py: small ? 2 : 4,
        px: small ? 3 : 5,
        background: base,
        '&:hover': {
          background: hover,
          '&[disabled]': {
            background: hover,
          },
        },
        '& svg *': {
          stroke: color,
        },
        ...props.sx,
      }}
    >
      {startAdornment && (
        <Box as="span" sx={{ mr: 2 }}>
          {startAdornment}
        </Box>
      )}
      {children}
      {endAdornment && (
        <Box as="span" sx={{ ml: 2 }}>
          {endAdornment}
        </Box>
      )}
    </CKButton>
  );
};
